<script setup>
import { computed, onMounted, ref } from 'vue';

const grants = ref([]);
const isLoading = ref(false);
const loadingGrantId = ref(null);
const errorMessage = ref(null);
const successMessage = ref(null);
const isTeleporting = ref(false);
const showTeleportOverlay = ref(false);
const teleportLabel = ref('');

const isSwitchedSession = computed(() => Boolean(window.myflow?.delegatedAccess?.switched));

const wait = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});

const runTeleportTransition = async ({ label, request, onError }) => {
    if (isTeleporting.value) {
        return;
    }

    isTeleporting.value = true;
    showTeleportOverlay.value = true;
    teleportLabel.value = label;

    try {
        const responsePromise = request();
        await wait(700);
        const response = await responsePromise;
        await wait(500);
        window.location.href = response.data.redirect ?? route('boIndex');
    } catch (error) {
        onError(error);
        showTeleportOverlay.value = false;
        teleportLabel.value = '';
        isTeleporting.value = false;
    }
};

const loadGrantedAccess = async () => {
    isLoading.value = true;
    errorMessage.value = null;

    try {
        const response = await axios.get(route('company.user.delegated-access.received'));
        grants.value = response.data.grants ?? [];
    } catch (error) {
        errorMessage.value = error.response?.data?.message_key
            ? _mft(error.response.data.message_key, error.response.data.message_args ?? {})
            : _mft('company:delegatedAccess.error.generic');
    } finally {
        isLoading.value = false;
    }
};

const switchToGrant = async (grant) => {
    loadingGrantId.value = grant.id;
    errorMessage.value = null;
    successMessage.value = null;

    await runTeleportTransition({
        label: _mft('navigation:backOffice.widget.teleportSwitching', {
            company: grant.company_name,
        }),
        request: () => axios.post(route('company.user.delegated-access.switch', grant.id)),
        onError: (error) => {
            errorMessage.value = error.response?.data?.message_key
                ? _mft(error.response.data.message_key, error.response.data.message_args ?? {})
                : _mft('company:delegatedAccess.error.generic');
            loadingGrantId.value = null;
        },
    });
};

const returnToOrigin = async () => {
    loadingGrantId.value = -1;
    errorMessage.value = null;
    successMessage.value = null;

    await runTeleportTransition({
        label: _mft('navigation:backOffice.widget.teleportReturning'),
        request: () => axios.post(route('company.user.delegated-access.return')),
        onError: (error) => {
            errorMessage.value = error.response?.data?.message_key
                ? _mft(error.response.data.message_key, error.response.data.message_args ?? {})
                : _mft('company:delegatedAccess.error.generic');
            loadingGrantId.value = null;
        },
    });
};

onMounted(() => {
    loadGrantedAccess();
});
</script>

<template>
    <card-basic>
        <template #body>
            <div class="space-y-6">
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <h1 class="mb-0">{{ _mft('company:delegatedAccess.received.title') }}</h1>
                    <button-base
                        v-if="isSwitchedSession"
                        style-type="warning"
                        type="button"
                        :state="loadingGrantId === -1 || isTeleporting ? 'waiting' : ''"
                        @click="returnToOrigin"
                    >
                        {{ _mft('company:delegatedAccess.action.returnToOrigin') }}
                    </button-base>
                </div>

                <p class="text-gray-600">{{ _mft('company:delegatedAccess.received.description') }}</p>

                <success-banner v-if="successMessage">
                    <template #header>
                        {{ _mft('company:delegatedAccess.successHeading') }}
                    </template>
                    <template #body>
                        {{ successMessage }}
                    </template>
                </success-banner>

                <danger-banner v-if="errorMessage">
                    <template #header>
                        {{ _mft('company:delegatedAccess.errorHeading') }}
                    </template>
                    <template #body>
                        {{ errorMessage }}
                    </template>
                </danger-banner>

                <div v-if="isLoading" class="text-sm text-gray-500">
                    {{ _mft('company:delegatedAccess.loading') }}
                </div>

                <ul v-else class="divide-y">
                    <li
                        v-for="grant in grants"
                        :key="grant.id"
                        class="flex flex-wrap items-center justify-between gap-4 py-3"
                    >
                        <div>
                            <div class="font-semibold">{{ grant.company_name }}</div>
                            <div class="text-xs text-gray-500">
                                {{ _mft('company:delegatedAccess.received.grantedBy') }}: {{ grant.granted_by_name }}
                            </div>
                        </div>
                        <button-base
                            style-type="success"
                            type="button"
                            :state="loadingGrantId === grant.id || isTeleporting ? 'waiting' : ''"
                            @click="switchToGrant(grant)"
                        >
                            {{ _mft('company:delegatedAccess.action.switch') }}
                        </button-base>
                    </li>
                    <li v-if="grants.length === 0" class="py-3 text-sm text-gray-500">
                        {{ _mft('company:delegatedAccess.received.empty') }}
                    </li>
                </ul>
            </div>
        </template>
    </card-basic>

    <transition name="teleport-fade">
        <div
            v-if="showTeleportOverlay"
            class="teleport-overlay"
        >
            <div class="teleport-vortex">
                <span class="teleport-ring teleport-ring-a"></span>
                <span class="teleport-ring teleport-ring-b"></span>
                <span class="teleport-ring teleport-ring-c"></span>
                <span class="teleport-core"></span>
            </div>
            <p class="teleport-label">{{ teleportLabel }}</p>
        </div>
    </transition>
</template>

<style scoped>
.teleport-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background:
        radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 0, rgba(255, 255, 255, 0) 40%),
        radial-gradient(circle at 75% 70%, rgba(145, 102, 160, 0.25) 0, rgba(145, 102, 160, 0) 45%),
        linear-gradient(145deg, rgba(8, 15, 45, 0.95), rgba(23, 5, 56, 0.96));
    backdrop-filter: blur(3px);
}

.teleport-vortex {
    position: relative;
    width: 10rem;
    height: 10rem;
}

.teleport-ring {
    position: absolute;
    inset: 0;
    border: 2px solid rgba(255, 255, 255, 0.45);
    border-radius: 9999px;
    mix-blend-mode: screen;
}

.teleport-ring-a {
    animation: teleportRing 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.teleport-ring-b {
    animation: teleportRing 1.1s cubic-bezier(0.4, 0, 0.2, 1) 0.2s infinite;
}

.teleport-ring-c {
    animation: teleportRing 1.1s cubic-bezier(0.4, 0, 0.2, 1) 0.4s infinite;
}

.teleport-core {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 9999px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(255, 255, 255, 0.96), rgba(212, 182, 255, 0.7));
    box-shadow: 0 0 22px rgba(210, 182, 255, 0.85);
    animation: teleportCore 0.9s ease-in-out infinite;
}

.teleport-label {
    margin-top: 1.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: #f8f9ff;
    text-shadow: 0 0 14px rgba(205, 175, 255, 0.55);
}

.teleport-fade-enter-active,
.teleport-fade-leave-active {
    transition: opacity 180ms ease;
}

.teleport-fade-enter-from,
.teleport-fade-leave-to {
    opacity: 0;
}

@keyframes teleportRing {
    0% {
        transform: scale(0.55);
        opacity: 0.95;
    }
    100% {
        transform: scale(1.35);
        opacity: 0;
    }
}

@keyframes teleportCore {
    0%, 100% {
        transform: translate(-50%, -50%) scale(0.85);
    }
    50% {
        transform: translate(-50%, -50%) scale(1.15);
    }
}
</style>
